import { BewegingGebondenKlusSoort } from "@personeel/components/CreateKlus/BewegingGebondenKlusSoort/BewegingGebondenKlusSoort.tsx";
import { KlusInputFormFieldNames } from "@personeel/enums/KlusInputFormFieldNames.ts";
import { useFetchLocaties } from "@personeel/hooks/useFetchData.ts";
import { KlusSoortModel } from "@personeel/models/KlusSoortModel.ts";
import { PersoneelsdienstenModel } from "@personeel/models/PersoneelsdienstenModel.ts";
import {
  RangeerbewegingenModel,
  RangeerbewegingModel,
} from "@personeel/models/RangeerBewegingModel.ts";
import { SpoorModel } from "@personeel/models/Spoor.ts";
import { usePersoneelsdienstenStore } from "@personeel/stores/PersoneelsdienstenStore.ts";
import { useRangeerbewegingenStore } from "@personeel/stores/RangeerBewegingenStore.ts";
import {
  addDays,
  addMinutes,
  calculateEndTimeWithPint,
  formatTime,
  isMidnightTransition,
  isValidTime,
} from "@personeel/utils/DateTimeUtils.ts";
import { isEindLocatieAnders } from "@personeel/utils/KlusUtils.ts";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore.ts";
import { DateTime, Interval } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { CreateKlusModel } from "../../models/CreateKlusModel.ts";
import { useKlusSoortenStore } from "../../stores/KlusSoortenStore.ts";
import { KlusInputErrors, valideerKlusInput } from "../../validators/KlusInputValidator.ts";
import "./CreateKlus.css";

interface CreateKlusProps {
  readonly addKlus: (klus: CreateKlusModel) => void;
  readonly cancel: () => void;
  readonly existingKlus?: CreateKlusModel | null;
}

function findRangeerbeweging(
  rangeerbewegingen: RangeerbewegingenModel,
  rangeerbewegingsactieId: string,
): RangeerbewegingModel | undefined {
  return rangeerbewegingen.find((rb) => rb.rangeerbewegingsactieId === rangeerbewegingsactieId);
}

function correctKlusEndtijd(klus: CreateKlusModel) {
  klus.eindDatum = bepaalEindDatum(klus.beginDatum, klus.beginTijd, klus.eindTijd);
}

function bepaalEindDatum(beginDatum: string, beginTijd: string, eindTijd: string) {
  if (
    beginDatum.trim() !== "" &&
    eindTijd.trim() !== "" &&
    beginTijd.trim() !== "" &&
    isMidnightTransition(beginTijd, eindTijd)
  ) {
    return addDays(beginDatum, 1);
  }
  return beginDatum;
}

function getFormGroupClassName(
  errors: KlusInputErrors,
  fieldName1: KlusInputFormFieldNames,
  fieldName2?: KlusInputFormFieldNames,
): string {
  const fieldnamesInErrors = fieldName2
    ? fieldName1 in errors || fieldName2 in errors
    : fieldName1 in errors;
  return `form-group ${fieldnamesInErrors ? "form-group-error" : ""}`;
}

function errorMessage(
  errors: KlusInputErrors,
  fieldName1: KlusInputFormFieldNames,
  fieldName2?: KlusInputFormFieldNames,
) {
  const showError1 = fieldName1 in errors;
  const showError2 = !!fieldName2 && fieldName2 in errors;

  if (!showError1 && !showError2) {
    return null;
  }

  return (
    <div className="error-margin">
      {showError1 && <tractie-error-message text={errors[fieldName1]} />}
      {showError2 && <tractie-error-message text={errors[fieldName2]} />}
    </div>
  );
}

function createAvailableDates(scenarioBeginDatum: DateTime, scenarioEindDatum: DateTime) {
  const availableDates = [];
  let mutableBeginDatum = scenarioBeginDatum.startOf("day");
  const lastAvailableDay = scenarioEindDatum.startOf("day");

  while (mutableBeginDatum <= lastAvailableDay) {
    availableDates.push({
      value: mutableBeginDatum.isValid ? (mutableBeginDatum.toISODate() ?? "") : "",
      label: mutableBeginDatum.toFormat("DD"),
    });
    mutableBeginDatum = mutableBeginDatum.plus({ days: 1 });
  }
  return availableDates;
}

type KlusInputElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export const CreateKlus: React.FC<CreateKlusProps> = ({ addKlus, existingKlus, cancel }) => {
  const [newKlus, setNewKlus] = useState<CreateKlusModel>(() =>
    existingKlus ? new CreateKlusModel(existingKlus) : new CreateKlusModel(),
  );

  const [errors, setErrors] = useState<KlusInputErrors>({});

  const klusSoorten = useKlusSoortenStore((state) => state.klusSoorten);
  const personeelsdiensten = usePersoneelsdienstenStore((state) => state.personeelsdiensten);

  const rangeerbewegingen = useRangeerbewegingenStore((state) => state.rangeerbewegingen);

  const [klusSoort, setKlusSoort] = useState<KlusSoortModel>();
  const [selectedBeweging, setSelectedBeweging] = useState<RangeerbewegingModel | undefined>();

  const scenarioBeginDatum: DateTime = useSelectedTimeStore().scenarioInterval!.start;
  const scenarioEindDatum: DateTime = useSelectedTimeStore().scenarioInterval!.end;
  const availableDates = createAvailableDates(scenarioBeginDatum, scenarioEindDatum);

  const locaties = useFetchLocaties();

  useEffect(() => {
    setErrors({});
    if (existingKlus) {
      setNewKlus(new CreateKlusModel(existingKlus));
      if (existingKlus.relatieActieId) {
        setSelectedBeweging(findRangeerbeweging(rangeerbewegingen, existingKlus.relatieActieId));
      } else {
        setSelectedBeweging(undefined);
      }
      setKlusSoort(klusSoorten.find((werk) => werk.klusCode == existingKlus.klusSoort));
    }
  }, [existingKlus, klusSoorten, rangeerbewegingen]);

  const submitRef = useRef<HTMLButtonElement>(null);

  const handleInputChange = (e: React.ChangeEvent<KlusInputElement>) => {
    const { name, value } = e.target;
    setNewKlus((prev: CreateKlusModel) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      return Object.fromEntries(Object.entries(prev).filter(([key]) => key !== name));
    });
  };

  const handleTimeChange = (e: React.ChangeEvent<KlusInputElement>) => {
    const { name, value } = e.target;

    const formattedTime = formatTime(value);
    if (formattedTime.length === 5 && !isValidTime(formattedTime)) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Ongeldige tijd ingevoerd.",
      }));
    } else {
      setErrors((prev) => {
        const fieldsToRemove = new Set([name, KlusInputFormFieldNames.Dienst]);
        return Object.fromEntries(Object.entries(prev).filter(([key]) => !fieldsToRemove.has(key)));
      });
    }

    setNewKlus((prev: CreateKlusModel) => ({
      ...prev,
      [name]: formattedTime,
    }));
  };

  const handleAdjustTime = (minutesToAdd: number) => {
    setNewKlus((prev: CreateKlusModel) => ({
      ...prev,
      [KlusInputFormFieldNames.Begintijd]: addMinutes(
        prev[KlusInputFormFieldNames.Begintijd],
        minutesToAdd,
      ),
      [KlusInputFormFieldNames.Eindtijd]: addMinutes(
        prev[KlusInputFormFieldNames.Eindtijd],
        minutesToAdd,
      ),
    }));
    setErrors((prev) => {
      return Object.fromEntries(
        Object.entries(prev).filter(([key]) => key !== (KlusInputFormFieldNames.Dienst as string)),
      );
    });
  };

  const handleBewegingSelected = (value: string) => {
    const beweging = findRangeerbeweging(rangeerbewegingen, value);
    setSelectedBeweging(beweging);
    if (!beweging) {
      return;
    }

    const eindtijd = calculateEndTimeWithPint(beweging.eind.tijd).toFormat("HH:mm");

    setNewKlus((prev: CreateKlusModel) => ({
      ...prev,
      [KlusInputFormFieldNames.Rangeerbeweging]: beweging.rangeerbewegingsactieId,
      [KlusInputFormFieldNames.Begintijd]: beweging.start.tijd.toFormat("HH:mm"),
      [KlusInputFormFieldNames.Eindtijd]: eindtijd,
      [KlusInputFormFieldNames.Begindatum]: beweging.start.tijd.toFormat("yyyy-MM-dd"),
      dienstregelpuntStart: beweging.start.hoofdspoor.dienstregelpunt,
      spoorStart: beweging.start.hoofdspoor.naam,
      dienstregelpuntEind: beweging.eind.hoofdspoor.dienstregelpunt,
      spoorEind: beweging.eind.hoofdspoor.naam,
      bewegingsCode: beweging.bewegingCode,
    }));
    setErrors({});
  };

  const handleKlussoortChange = (e: React.ChangeEvent<KlusInputElement>) => {
    setNewKlus(new CreateKlusModel());
    setSelectedBeweging(undefined);
    handleInputChange(e);
    setKlusSoort(klusSoorten.find((werk) => werk.klusCode == e.target.value));
    setErrors({});
  };

  const handleSpoorStartChange = (e: React.ChangeEvent<KlusInputElement>) => {
    const spoor = e.target.value.split(" ");
    const spoorModel: SpoorModel = { naam: spoor[1], dienstregelpunt: spoor[0] };
    newKlus.spoorStart = spoorModel.naam;
    newKlus.dienstregelpuntStart = spoorModel.dienstregelpunt;
    if (!isEindLocatieAnders(klusSoort)) {
      newKlus.spoorEind = spoorModel.naam;
      newKlus.dienstregelpuntEind = spoorModel.dienstregelpunt;
    }
    setErrors({});
  };

  const handleSpoorEindChange = (e: React.ChangeEvent<KlusInputElement>) => {
    const spoor = e.target.value.split(" ");
    const spoorModel: SpoorModel = { naam: spoor[1], dienstregelpunt: spoor[0] };
    newKlus.spoorEind = spoorModel.naam;
    newKlus.dienstregelpuntEind = spoorModel.dienstregelpunt;
    setErrors({});
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    correctKlusEndtijd(newKlus);

    const errors = valideerKlusInput(newKlus);

    if (Object.keys(errors).length === 0) {
      setErrors({});
      addKlus(newKlus);
      setNewKlus(new CreateKlusModel());
      setKlusSoort(undefined);
    } else {
      setErrors(errors);
    }
  };

  const handleAnnuleren = () => {
    setNewKlus(new CreateKlusModel());
    setKlusSoort(undefined);
    cancel();
  };

  const beschikbareLocaties =
    newKlus.klusSoort === "SCHAFT" ? locaties.data?.personeelsverblijven : locaties.data?.sporen;

  const filterDiensten = (pdd: PersoneelsdienstenModel) => {
    if (
      newKlus.beginDatum.trim() === "" ||
      newKlus.beginTijd.trim() === "" ||
      newKlus.eindTijd.trim() === ""
    ) {
      return [];
    }

    const klusEindDatum = bepaalEindDatum(newKlus.beginDatum, newKlus.beginTijd, newKlus.eindTijd);

    return pdd.personeelsdiensten.filter((werkp) =>
      Interval.fromDateTimes(werkp.starttijd, werkp.eindtijd).engulfs(
        Interval.fromDateTimes(
          DateTime.fromFormat(newKlus.beginDatum + newKlus.beginTijd, "yyyy-MM-ddHH:mm"),
          DateTime.fromFormat(klusEindDatum + newKlus.eindTijd, "yyyy-MM-ddHH:mm"),
        ),
      ),
    );
  };

  return (
    <div data-testid="form-create-klus" data-mode={newKlus.id ? "edit" : "create"}>
      {!newKlus.id && <h2 data-testid="title-create-klus">Klus aanmaken</h2>}
      {newKlus.id && <h2 data-testid="title-edit-klus">Klus wijzigen</h2>}

      <form
        data-testid="form-create-klus-fields"
        onSubmit={(e) => {
          handleSubmit(e);
        }}
      >
        <div className={getFormGroupClassName(errors, KlusInputFormFieldNames.KlusSoort)}>
          {newKlus.id && (
            <div>
              <tractie-label label="Klus"></tractie-label>
              <div>{newKlus.klusSoort}</div>
            </div>
          )}
          {!newKlus.id && (
            <tractie-select
              label={"Klus"}
              modelValue={newKlus.klusSoort}
              name={KlusInputFormFieldNames.KlusSoort}
              data-testid="select-klussoort"
            >
              <select
                className={
                  KlusInputFormFieldNames.KlusSoort in errors ? "form-control error-border" : ""
                }
                slot="input"
                value={newKlus.klusSoort}
                onChange={(change) => {
                  handleKlussoortChange(change);
                }}
                id={KlusInputFormFieldNames.KlusSoort}
                name={KlusInputFormFieldNames.KlusSoort}
              >
                <option value="">Kies klus</option>
                {klusSoorten.map((klusSoort) => (
                  <option key={klusSoort.klusCode} value={klusSoort.klusCode}>
                    {klusSoort.klusCode}
                  </option>
                ))}
              </select>
            </tractie-select>
          )}
        </div>
        {errorMessage(errors, KlusInputFormFieldNames.KlusSoort)}

        {klusSoort?.klusType === "BEWEGINGSGEBONDEN" && (
          <BewegingGebondenKlusSoort
            errorMessage={errors[KlusInputFormFieldNames.Rangeerbeweging]}
            selectedBeweging={selectedBeweging}
            selectedRangeerbewegingsactieId={newKlus.relatieActieId}
            onBewegingSelected={(rangeerbewegingsactieId) => {
              handleBewegingSelected(rangeerbewegingsactieId);
            }}
          />
        )}

        <div className="form-time-selector">
          <div
            className={
              getFormGroupClassName(errors, KlusInputFormFieldNames.Begintijd) + " form-group-small"
            }
          >
            <tractie-input
              label={"Starttijd"}
              modelValue={newKlus.beginTijd}
              data-testid="input-begintijd"
              type={KlusInputFormFieldNames.Begintijd in errors ? "error" : "success"}
            >
              <input
                slot="input"
                name={KlusInputFormFieldNames.Begintijd}
                id={KlusInputFormFieldNames.Begintijd}
                type="text"
                value={newKlus.beginTijd}
                onChange={handleTimeChange}
                className="tractie-input"
                placeholder="hh:mm"
                maxLength={5}
              />
            </tractie-input>
          </div>
          <div
            className={
              getFormGroupClassName(errors, KlusInputFormFieldNames.SpoorStart) +
              " form-group-large"
            }
          >
            <tractie-select
              label={"Startlocatie"}
              modelValue={newKlus.dienstregelpuntStart + " " + newKlus.spoorStart}
              name={KlusInputFormFieldNames.SpoorStart}
              data-testid="select-spoorstart"
            >
              <select
                slot="input"
                value={newKlus.dienstregelpuntStart + " " + newKlus.spoorStart}
                onChange={handleSpoorStartChange}
                className={`form-control${KlusInputFormFieldNames.SpoorStart in errors ? " error-border" : ""}`}
              >
                <option value="">Kies spoor</option>
                {beschikbareLocaties?.map((spoor) => (
                  <option
                    key={spoor.dienstregelpunt + spoor.naam}
                    value={spoor.dienstregelpunt + " " + spoor.naam}
                  >
                    {spoor.dienstregelpunt + " " + spoor.naam}
                  </option>
                ))}
              </select>
            </tractie-select>
          </div>
        </div>
        {errorMessage(
          errors,
          KlusInputFormFieldNames.Begintijd,
          KlusInputFormFieldNames.SpoorStart,
        )}
        <div className="form-time-selector">
          <div
            className={
              getFormGroupClassName(errors, KlusInputFormFieldNames.Eindtijd) + " form-group-small"
            }
          >
            <tractie-input
              label={"Eindtijd"}
              modelValue={newKlus.eindTijd}
              data-testid="input-eindtijd"
              type={KlusInputFormFieldNames.Eindtijd in errors ? "error" : "success"}
            >
              <input
                slot="input"
                name={KlusInputFormFieldNames.Eindtijd}
                id={KlusInputFormFieldNames.Eindtijd}
                type="text"
                value={newKlus.eindTijd}
                onChange={handleTimeChange}
                className="tractie-input"
                maxLength={5}
                placeholder="hh:mm"
              />
            </tractie-input>
          </div>
          <div
            className={
              getFormGroupClassName(errors, KlusInputFormFieldNames.SpoorEind) + " form-group-large"
            }
          >
            {isEindLocatieAnders(klusSoort) && (
              <tractie-select
                label={"Eindlocatie"}
                modelValue={newKlus.dienstregelpuntEind + " " + newKlus.spoorEind}
                name={"spoorEind"}
                data-testid="select-spooreind"
              >
                <select
                  slot="input"
                  value={newKlus.dienstregelpuntEind + " " + newKlus.spoorEind}
                  onChange={handleSpoorEindChange}
                  className={`form-control${KlusInputFormFieldNames.SpoorEind in errors ? " error-border" : ""}`}
                >
                  <option value="">Kies spoor</option>
                  {beschikbareLocaties?.map((spoor) => (
                    <option
                      key={spoor.dienstregelpunt + spoor.naam}
                      value={spoor.dienstregelpunt + " " + spoor.naam}
                    >
                      {spoor.dienstregelpunt + " " + spoor.naam}
                    </option>
                  ))}
                </select>
              </tractie-select>
            )}
            {!isEindLocatieAnders(klusSoort) && (
              <div>
                <tractie-label label="Eindlocatie"></tractie-label>
                <div className="empty-location">-</div>
              </div>
            )}
          </div>
        </div>
        {errorMessage(errors, KlusInputFormFieldNames.Eindtijd, KlusInputFormFieldNames.SpoorEind)}

        <div className="form-time-selector">
          <div className="button-group">
            <tractie-button
              level="secondary"
              className="time-button"
              data-testid="time-minus-button"
              onClick={() => handleAdjustTime(-1)}
            >
              <tractie-icon name="minus" />
            </tractie-button>
            <tractie-button
              level="secondary"
              className="time-button"
              data-testid="time-plus-button"
              onClick={() => handleAdjustTime(1)}
            >
              <tractie-icon name="plus" />
            </tractie-button>
          </div>
        </div>

        <div className={getFormGroupClassName(errors, KlusInputFormFieldNames.Begindatum)}>
          <tractie-select
            label={"Klus begint op"}
            modelValue={newKlus.beginDatum}
            name={KlusInputFormFieldNames.Begindatum}
            data-testid="select-begindatum"
          >
            <select
              name={KlusInputFormFieldNames.Begindatum}
              className={`form-control${KlusInputFormFieldNames.Begindatum in errors ? " error-border" : ""}`}
              slot="input"
              value={newKlus.beginDatum}
              onChange={handleInputChange}
            >
              <option value="">Kies dag</option>
              {availableDates.map((scenarioDate) => (
                <option key={scenarioDate.value} value={scenarioDate.value}>
                  {scenarioDate.label}
                </option>
              ))}
            </select>
          </tractie-select>
        </div>
        {errorMessage(errors, KlusInputFormFieldNames.Begindatum)}

        <div className={getFormGroupClassName(errors, KlusInputFormFieldNames.Dienst)}>
          <tractie-select
            label={"Dienst"}
            modelValue={newKlus.dienst}
            name={KlusInputFormFieldNames.Dienst}
            data-testid="select-dienst"
          >
            <select
              name={KlusInputFormFieldNames.Dienst}
              slot="input"
              value={newKlus.dienst}
              onChange={handleInputChange}
            >
              <option value="">Niet toegewezen</option>
              {personeelsdiensten.flatMap((pdd) =>
                filterDiensten(pdd).map((werkperiode) => {
                  return (
                    <option key={werkperiode.id} value={werkperiode.id}>
                      {werkperiode.label}
                    </option>
                  );
                }),
              )}
            </select>
          </tractie-select>
        </div>
        {errorMessage(errors, KlusInputFormFieldNames.Dienst)}

        <div className="form-group">
          <tractie-textarea
            label={"Toelichting"}
            id="toelichting"
            name="toelichting"
            data-testid="textarea-toelichting"
            modelValue={newKlus.toelichting}
            onChange={handleInputChange}
            className={"tractie-textarea"}
          />
        </div>
        <div className="form-row">
          <button type="submit" ref={submitRef} style={{ display: "none" }} />
          <tractie-button
            level="primary"
            data-testid="submit-klus-button"
            onClick={() => submitRef.current?.click()}
          >
            {!newKlus.id && <span>Aanmaken</span>}
            {newKlus.id && <span>Wijzigen</span>}
          </tractie-button>
          <tractie-button
            level="tertiary"
            data-testid="cancel-klus-button"
            onClick={handleAnnuleren}
          >
            <span>Annuleren</span>
          </tractie-button>
        </div>
      </form>
    </div>
  );
};
